import { makeStyles } from "@fluentui/react-components";
import { MagnifyingGlassIcon } from "@phosphor-icons/react";
import { tokens } from "../../theme";

// =============================================================================
// TOKEN SHORTCUTS
// =============================================================================

const t = {
    // Spacing
    spacingVerticalM: tokens.primitives["spacing-Vertical-M"],
    spacingHorizontalM: tokens.primitives["spacing-Horizontal-M"],
    spacingHorizontalL: tokens.primitives["spacing-Horizontal-L"],

    // Typography
    fontFamilyBase: tokens.primitives["font-Family-Base"],
    fontSizeBase400: tokens.primitives["font-Size-Base-400"],
    fontWeightRegular: tokens.primitives["font-Weight-Regular"],
    lineHeightBase400: tokens.primitives["lineHeight-Base-400"],

    // Colors
    accentPrimary: tokens.palette["accent-Primary"],
    accentDark: tokens.palette["accent-Dark"],
    accentLighterAlt: tokens.palette["accent-LighterAlt"],
    accentLighter: tokens.palette["accent-Lighter"],
    neutralWhite: tokens.semantic["neutral-White"],
    accentPrimaryStroke: tokens.semantic["brandAccentPrimaryStroke"],
    accentSecondaryFg: tokens.semantic["brandAccentSecondaryForeground"],

    // Radius
    radiusLarge: tokens.primitives["stroke-Radius-Large"],
    radiusXLarge: tokens.primitives["stroke-Radius-XLarge"],
    radiusCircular: tokens.primitives["stroke-Radius-Circular"],

    shadowRestingXs: tokens.shadows["shadow-Resting-Xs"]
};

// =============================================================================
// STYLES
// =============================================================================

const useStyles = makeStyles({
    wrapper: {
        display: "flex",
        justifyContent: "flex-end",
        width: "100%",

        "@media (max-width: 768px)": {
            paddingLeft: "16px" // Creates left margin, pushes content right
        }
    },

    container: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: `${t.spacingVerticalM} ${t.spacingHorizontalM}`,
        width: "100%",
        maxWidth: "507px",
        backgroundColor: t.accentLighterAlt,
        borderRadius: t.radiusXLarge,
        padding: `${t.spacingVerticalM} ${t.spacingHorizontalL}`,
        boxSizing: "border-box",
        boxShadow: t.shadowRestingXs,
        border: `1px solid ${t.accentPrimaryStroke}`
    },

    iconContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "40px",
        height: "40px",
        borderRadius: t.radiusCircular,
        backgroundColor: t.accentSecondaryFg,
        color: t.neutralWhite,
        flexShrink: 0,

        "@media (max-width: 768px)": {
            width: "24px",
            height: "24px"
        }
    },

    magnifyingGlassIcon: {
        "@media (max-width: 768px)": {
            width: "16px",
            height: "16px"
        }
    },

    messageContent: {
        fontFamily: t.fontFamilyBase,
        fontSize: t.fontSizeBase400,
        fontWeight: t.fontWeightRegular as unknown as number,
        lineHeight: t.lineHeightBase400,
        color: t.accentSecondaryFg,
        wordBreak: "break-word"
    }
});

// =============================================================================
// COMPONENT
// =============================================================================

interface Props {
    message: string;
}

export const UserChatMessage = ({ message }: Props) => {
    const styles = useStyles();

    return (
        <div className={styles.wrapper} aria-label="Your question">
            <div className={styles.container}>
                <div className={styles.iconContainer} aria-hidden="true">
                    <MagnifyingGlassIcon size={26} weight="regular" className={styles.magnifyingGlassIcon} />
                </div>
                <div className={styles.messageContent}>{message}</div>
            </div>
        </div>
    );
};
